import { MAX_REPORT_BYTES, fetchData, readResponseJson } from './limits'
import { webAddress } from './normalise'
import type { PlatformProvider, RepositoryRef } from './repository'

/**
 * Finding assessments that have already been published.
 *
 * Everything here reads flat files from a content delivery network rather than
 * querying an API. That is not an aesthetic preference: the platform API allows
 * sixty unauthenticated requests an hour, and a browser application holds no
 * credential, so anything built on it would stop working for users on a shared
 * address. Static files served from a CDN have no such limit.
 *
 * Sources are described by data, not code, so adding one -- an institution's
 * own index, say -- needs no release.
 */

type SourceKind = 'index' | 'repository' | 'urls' | 'zenodo'

export interface CatalogSource {
  id: string
  name: string
  kind: SourceKind
  /** An index file, for `index`. */
  url?: string
  /** owner/name or group/subgroup/project, for `repository`. */
  repository?: string
  /** Platform provider: 'github' | 'gitlab' */
  provider?: PlatformProvider
  /** Host domain for self-hosted instances (default: github.com or gitlab.com) */
  host?: string
  /** Branch / ref, defaults to 'HEAD' (GitHub) or 'main' (GitLab) */
  ref?: string
  /** Report URLs, for `urls`. */
  urls?: string[]
  /** Search terms, for `zenodo`. */
  query?: string
  description?: string
  /** False for sources the user added and has switched off. */
  enabled?: boolean
  /** The source whose community list named this one, when a registry did. */
  listedBy?: string
  /** What a registry says about a community source: who runs it, and whether it checked out. */
  about?: SourceAbout
  /** Last synced timestamp */
  lastSyncedAt?: number
}

const KINDS: readonly string[] = ['index', 'repository', 'urls', 'zenodo']

const isHttpUrl = (value: unknown): boolean => webAddress(value) !== undefined
const optional = (value: unknown, check: (v: unknown) => boolean) =>
  value === undefined || check(value)
const isString = (v: unknown) => typeof v === 'string'
/** A path on this site (the bundled samples), or a web address; no other scheme. */
const isReportAddress = (v: unknown) =>
  isString(v) && (!/^[a-z][a-z0-9+.-]*:/i.test(v as string) || isHttpUrl(v))

/**
 * A host a community list may send this browser to: a public name, not this
 * machine or an address on the local network, which a list written by anyone
 * could otherwise probe through every visitor who enables it.
 */
export function isPublicHost(host: string): boolean {
  const name = host.toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '')
  return (
    name.includes('.') &&
    !/^[\d.]+$/.test(name) &&
    !name.includes(':') &&
    !name.startsWith('[') &&
    !/(^|\.)(localhost|local|internal|home\.arpa)$/.test(name)
  )
}

/** A usable source that would only send requests to public hosts: its index's, or its own. */
function isListable(source: unknown): source is CatalogSource {
  if (!isCatalogSource(source)) return false
  if (source.url !== undefined && !isPublicHost(new URL(source.url).host)) return false
  return source.host === undefined || isPublicHost(source.host)
}

/**
 * Whether a stored or imported value is a usable source.
 *
 * Sources decide what the application fetches, and they can arrive in a
 * session file someone else wrote, so every field that becomes an address is
 * checked to be one.
 */
export function isCatalogSource(value: unknown): value is CatalogSource {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    isString(v.id) &&
    isString(v.name) &&
    KINDS.includes(v.kind as string) &&
    optional(v.url, isHttpUrl) &&
    optional(v.urls, (u) => Array.isArray(u) && u.every(isReportAddress)) &&
    optional(v.repository, (r) => isString(r) && /^\w[\w.-]*(\/\w[\w.-]*)+$/.test(r as string)) &&
    optional(v.provider, (p) => p === 'github' || p === 'gitlab') &&
    optional(v.host, (h) => isString(h) && /^[a-z0-9.-]+(:\d+)?$/i.test(h as string)) &&
    optional(v.ref, isString) &&
    optional(v.query, isString) &&
    optional(v.enabled, (e) => typeof e === 'boolean') &&
    optional(v.listedBy, isString) &&
    optional(v.about, isSourceAbout)
  )
}

export interface SourceAbout {
  maintainer: string
  maintainerUrl?: string
  homepage?: string
  keywords?: string[]
  /** Set by the registry's own checks, not by whoever listed the source. */
  verification?: 'verified' | 'failing' | 'unchecked'
  checkedAt?: string
  assessments?: number
  software?: number
}

const VERIFICATION: readonly string[] = ['verified', 'failing', 'unchecked']

function isSourceAbout(value: unknown): value is SourceAbout {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    isString(v.maintainer) &&
    optional(v.maintainerUrl, isHttpUrl) &&
    optional(v.homepage, isHttpUrl) &&
    optional(v.keywords, (k) => Array.isArray(k) && k.every(isString)) &&
    optional(v.verification, (s) => VERIFICATION.includes(s as string)) &&
    optional(v.checkedAt, isString) &&
    optional(v.assessments, (n) => typeof n === 'number') &&
    optional(v.software, (n) => typeof n === 'number')
  )
}

export interface CatalogEntry {
  sourceId: string
  sourceName: string
  softwareName: string
  softwareVersion?: string
  repositoryUrl?: string
  /** Where the assessment document itself can be fetched. */
  reportUrl: string
  assessedAt?: string
  checks?: number
}

export interface SourceResult {
  source: CatalogSource
  entries: CatalogEntry[]
  error?: string
  /** Set when the source listed more than is taken from it, saying so. */
  notice?: string
}

/**
 * The most assessments taken from one source. Each becomes a fetch, and an
 * index is someone else's file that can list as many as it likes.
 */
export const MAX_ENTRIES_PER_SOURCE = 500

/** The newest entries up to the cap, in the order the source gave them. */
function capped(source: CatalogSource, entries: CatalogEntry[]): SourceResult {
  if (entries.length <= MAX_ENTRIES_PER_SOURCE) return { source, entries }
  // Undated entries sort last; with no dates at all this keeps the first ones.
  const newest = new Set(
    entries
      .toSorted((a, b) => (b.assessedAt ?? '').localeCompare(a.assessedAt ?? ''))
      .slice(0, MAX_ENTRIES_PER_SOURCE),
  )
  return {
    source,
    entries: entries.filter((entry) => newest.has(entry)),
    notice: `Lists ${entries.length} assessments; only the newest ${MAX_ENTRIES_PER_SOURCE} are offered.`,
  }
}

/** The agreed location for assessments committed into the assessed repository. */
export const WELL_KNOWN_DIR = '.everse/assessments'
export const WELL_KNOWN_INDEX = `${WELL_KNOWN_DIR}/index.json`

/** The EVERSE Assessment Registry, shipped in src/data/catalog-sources.json. */
export const REGISTRY_ID = 'everse-registry'

/** Where a registry lists the community sources it knows of. */
const SOURCE_LIST = '.everse/sources.json'

/** Sources taken from one list, so a list cannot flood the Sources page. */
export const MAX_LISTED_SOURCES = 100

/** The format and version of a community source list this application reads. */
const SOURCE_LIST_FORMAT = { format: 'everse-source-list', version: 1 }

/** Text a registry gave, trimmed and of a sane length, or nothing. */
const trimmed = (value: unknown, max: number) =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined

/**
 * The community sources a registry lists, as sources of this application.
 *
 * Someone else's file, so it must declare the format read here, and each
 * entry must say what it is (a name, a description and who maintains it)
 * and point at a well-formed repository or index; anything else is left
 * out. Every entry arrives switched off: listing a source never makes a
 * browser fetch from it, only the user enabling it does. Ids are derived
 * from what the entry points at, under a prefix of their own, so a list can
 * neither replace a source the user or the application already has, nor
 * move an earlier choice to enable one onto somewhere else.
 *
 * The registry's own checks (verification) are shown, not trusted: the
 * entry is still validated here, and only fetched once enabled.
 */
export function readSourceList(value: unknown, listedBy: string): CatalogSource[] {
  const file = record<{ format?: unknown; version?: unknown; sources?: Record<string, unknown>[] }>(
    value,
  )
  if (file.format !== SOURCE_LIST_FORMAT.format || file.version !== SOURCE_LIST_FORMAT.version) {
    return []
  }
  const seen = new Set<string>()
  const read: CatalogSource[] = []
  for (const entry of list(file.sources)) {
    if (read.length >= MAX_LISTED_SOURCES) break
    const kind =
      entry.kind === 'index' ? 'index' : entry.kind === 'repository' ? 'repository' : undefined
    const target = kind === 'index' ? entry.url : entry.repository
    const name = trimmed(entry.name, 120)
    const description = trimmed(entry.description, 500)
    const maintainer = record<Record<string, unknown>>(entry.maintainer)
    const maintainerName = trimmed(maintainer.name, 120)
    if (!kind || typeof target !== 'string' || !name || !description || !maintainerName) continue
    const provider =
      entry.provider === 'gitlab' ? 'gitlab' : kind === 'repository' ? 'github' : undefined
    const check = record<Record<string, unknown>>(entry.verification)
    const about: SourceAbout = {
      maintainer: maintainerName,
      ...(webAddress(maintainer.url) ? { maintainerUrl: webAddress(maintainer.url) } : {}),
      ...(webAddress(entry.homepage) ? { homepage: webAddress(entry.homepage) } : {}),
      ...(Array.isArray(entry.keywords)
        ? {
            keywords: entry.keywords
              .map((k) => trimmed(k, 40))
              .filter((k): k is string => Boolean(k))
              .slice(0, 8),
          }
        : {}),
      verification: VERIFICATION.includes(check.status as string)
        ? (check.status as SourceAbout['verification'])
        : 'unchecked',
      ...(trimmed(check.checkedAt, 40) ? { checkedAt: trimmed(check.checkedAt, 40) } : {}),
      ...(typeof check.assessments === 'number' ? { assessments: check.assessments } : {}),
      ...(typeof check.software === 'number' ? { software: check.software } : {}),
    }
    const source = {
      id: `community:${kind}:${String(entry.host ?? '')}${target}`.toLowerCase(),
      name,
      kind,
      ...(kind === 'index' ? { url: target } : { repository: target, provider }),
      ...(typeof entry.host === 'string' ? { host: entry.host } : {}),
      ...(typeof entry.ref === 'string' ? { ref: entry.ref } : {}),
      description,
      enabled: false,
      listedBy,
      about,
    }
    if (!isListable(source) || seen.has(source.id)) continue
    seen.add(source.id)
    read.push(source)
  }
  return read
}

/** A repository source's list of community sources, or nothing when it publishes none. */
export async function loadSourceList(
  registry: CatalogSource,
  fetchImpl: typeof fetch = fetch,
): Promise<CatalogSource[]> {
  if (registry.kind !== 'repository' || !registry.repository) return []
  const provider = registry.provider ?? 'github'
  const host = registry.host ?? (provider === 'gitlab' ? 'gitlab.com' : 'github.com')
  const ref = registry.ref ?? (provider === 'gitlab' ? 'main' : 'HEAD')
  const file = await fetchJson(
    rawUrl(registry.repository, SOURCE_LIST, ref, provider, host),
    fetchImpl,
  )
  return readSourceList(file, registry.id)
}

const GITHUB_RAW = 'https://raw.githubusercontent.com'

export function rawUrl(
  repository: string,
  path: string,
  ref = 'HEAD',
  provider: PlatformProvider = 'github',
  host?: string,
): string {
  if (provider === 'gitlab') {
    const h = host ?? 'gitlab.com'
    return `https://${h}/${repository}/-/raw/${ref}/${path}`
  }
  return `${GITHUB_RAW}/${repository}/${ref}/${path}`
}

/**
 * Loads every source, independently.
 *
 * One unreachable source must not cost the user the others, so failures are
 * captured per source and reported alongside whatever did load.
 */
export async function loadSources(
  sources: CatalogSource[],
  fetchImpl: typeof fetch = fetch,
): Promise<SourceResult[]> {
  const active = sources.filter((source) => source.enabled !== false)
  return Promise.all(active.map((source) => loadSource(source, fetchImpl)))
}

async function loadSource(source: CatalogSource, fetchImpl: typeof fetch): Promise<SourceResult> {
  try {
    const read = (entries: CatalogEntry[]) => capped(source, entries.flatMap(clean))
    switch (source.kind) {
      case 'index':
        return read(await loadIndex(source, fetchImpl))
      case 'repository':
        return read(await loadRepository(source, fetchImpl))
      case 'urls':
        return read(loadUrls(source))
      case 'zenodo':
        return read(await loadZenodo(source, fetchImpl))
      default:
        return { source, entries: [], error: `Unknown source type "${source.kind}".` }
    }
  } catch (error) {
    return { source, entries: [], error: describe(error) }
  }
}

async function loadIndex(source: CatalogSource, fetchImpl: typeof fetch): Promise<CatalogEntry[]> {
  if (!source.url) throw new Error('This source has no address.')
  return entriesOf(source, readAssessmentIndex(await fetchJson(source.url, fetchImpl), source.url))
}

const entriesOf = (source: CatalogSource, listed: IndexedAssessment[]): CatalogEntry[] =>
  listed.map((entry) => ({ sourceId: source.id, sourceName: source.name, ...entry }))

/** One assessment an index lists, every field of the type it says. */
export type IndexedAssessment = Omit<CatalogEntry, 'sourceId' | 'sourceName'>

/**
 * The assessments an index file lists, read the one way every caller reads it.
 *
 * Two shapes: one repository's own results (`software` and `assessments`, each
 * a bare `file` in the index's folder or a `url`), or several repositories'
 * (`projects`, each with its own `assessments` by `url`). Addresses are
 * relative to `indexUrl` and must end up as web addresses; any field of the
 * wrong type is dropped, and an entry with no address is left out. The file is
 * someone else's, so nothing in it is trusted to be what it claims.
 *
 * @param fallback What a single-repository index stands for when it does not
 *   say itself: the repository's name and address.
 */
export function readAssessmentIndex(
  value: unknown,
  indexUrl: string,
  fallback: { softwareName?: string; repositoryUrl?: string } = {},
): IndexedAssessment[] {
  const index = record<Record<string, unknown>>(value)
  const address = (url: unknown) =>
    text(url) ? webAddress(absolute(text(url)!, indexUrl)) : undefined
  const entry = (
    assessment: Record<string, unknown>,
    reportUrl: string | undefined,
    software: { name?: string; url?: string },
  ): IndexedAssessment[] =>
    reportUrl
      ? [
          {
            softwareName: software.name ?? 'Unnamed software',
            softwareVersion: text(assessment.softwareVersion),
            repositoryUrl: software.url,
            reportUrl,
            assessedAt: text(assessment.assessedAt),
            checks: typeof assessment.checks === 'number' ? assessment.checks : undefined,
          },
        ]
      : []

  if (Array.isArray(index.projects)) {
    return list(index.projects as Record<string, unknown>[]).flatMap((project) =>
      list(project.assessments as Record<string, unknown>[]).flatMap((assessment) =>
        entry(assessment, address(assessment.url), {
          name: text(project.name),
          url: webAddress(project.url) ?? webAddress(project.repository),
        }),
      ),
    )
  }
  const software = record<Record<string, unknown>>(index.software)
  return list(index.assessments as Record<string, unknown>[]).flatMap((assessment) =>
    entry(
      assessment,
      isAssessmentFile(assessment.file) ? address(assessment.file) : address(assessment.url),
      {
        name: text(software.name) ?? fallback.softwareName,
        url: webAddress(software.url) ?? webAddress(fallback.repositoryUrl),
      },
    ),
  )
}

const text = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined

/**
 * Reads the index a project publishes into its own repository.
 *
 * Fetched from the raw content host rather than the API, so it is not subject
 * to the hourly request budget.
 */
async function loadRepository(
  source: CatalogSource,
  fetchImpl: typeof fetch,
): Promise<CatalogEntry[]> {
  if (!source.repository) throw new Error('This source names no repository.')

  const provider =
    source.provider ??
    (source.url?.includes('gitlab') || source.host?.includes('gitlab') ? 'gitlab' : 'github')
  const host = source.host ?? (provider === 'gitlab' ? 'gitlab.com' : 'github.com')
  const ref = source.ref ?? (provider === 'gitlab' ? 'main' : 'HEAD')

  const indexUrl = rawUrl(source.repository, WELL_KNOWN_INDEX, ref, provider, host)
  const file = await fetchJson(indexUrl, fetchImpl).catch(() => {
    throw new Error(
      `No assessments published at ${WELL_KNOWN_INDEX} in ${source.repository}. The project may not have adopted the workflow yet.`,
    )
  })
  return entriesOf(
    source,
    readAssessmentIndex(file, indexUrl, {
      softwareName: source.repository.split('/').pop(),
      repositoryUrl: `https://${host}/${source.repository}`,
    }),
  )
}

function loadUrls(source: CatalogSource): CatalogEntry[] {
  return (source.urls ?? []).map((url) => ({
    sourceId: source.id,
    sourceName: source.name,
    softwareName:
      url
        .split('/')
        .pop()
        ?.replace(/\.json$/i, '') ?? 'Assessment',
    reportUrl: url,
  }))
}

interface ZenodoResponse {
  hits?: {
    hits?: {
      metadata?: { title?: string; version?: string; publication_date?: string }
      files?: { key?: string; links?: { self?: string } }[]
      links?: { self_html?: string }
    }[]
  }
}

/**
 * Deposits matching a search.
 *
 * Only JSON files are offered, and whether one is actually an assessment is
 * settled when it is loaded, not guessed at from its name.
 */
async function loadZenodo(source: CatalogSource, fetchImpl: typeof fetch): Promise<CatalogEntry[]> {
  if (!source.query) throw new Error('This source has no search terms.')
  const url = `https://zenodo.org/api/records?q=${encodeURIComponent(source.query)}&size=25`
  const response = record<ZenodoResponse>(await fetchJson(url, fetchImpl))

  const entries: CatalogEntry[] = []
  for (const hit of list(response.hits?.hits)) {
    for (const file of list(hit.files)) {
      if (!file.links?.self || !file.key?.toLowerCase().endsWith('.json')) continue
      entries.push({
        sourceId: source.id,
        sourceName: source.name,
        softwareName: hit.metadata?.title ?? file.key,
        softwareVersion: hit.metadata?.version,
        repositoryUrl: webAddress(hit.links?.self_html),
        reportUrl: file.links.self,
        assessedAt: hit.metadata?.publication_date,
      })
    }
  }
  return entries
}

async function fetchJson(url: string, fetchImpl: typeof fetch): Promise<unknown> {
  const response = await fetchData(url, {}, fetchImpl)
  if (!response.ok) throw new Error(`The server answered ${response.status}.`)
  return readResponseJson(response, MAX_REPORT_BYTES)
}

/** Index entries may be relative to the index that lists them. */
function absolute(url: string, base: string): string {
  try {
    return new URL(url, base).toString()
  } catch {
    return url
  }
}

/**
 * A blocked cross-origin request and a network outage both surface as the same
 * opaque TypeError, so the wording covers both rather than guessing wrongly.
 */
export const UNREACHABLE =
  'Could not be fetched. The host may not allow other sites to read it, or the network may be unavailable.'

function describe(error: unknown): string {
  if (error instanceof TypeError) return UNREACHABLE
  return error instanceof Error ? error.message : 'This source could not be read.'
}

/**
 * How many assessments a repository publishes at the well-known path.
 *
 * Zero when it publishes none, or nothing there could be read as an index.
 * Throws only when the network itself failed, so the caller can tell "not
 * publishing" from "could not check".
 */
export async function countPublished(
  ref: RepositoryRef,
  fetchImpl: typeof fetch = fetch,
): Promise<number> {
  const indexUrl = rawUrl(ref.repository, WELL_KNOWN_INDEX, ref.ref, ref.provider, ref.host)
  const response = await fetchData(indexUrl, {}, fetchImpl)
  if (!response.ok) return 0
  return readAssessmentIndex(await readResponseJson(response, MAX_REPORT_BYTES), indexUrl).length
}

/**
 * Every report a repository publishes, read through its index as a source
 * would read it. For loading a repository once without adding it as a source.
 */
export async function publishedReports(
  ref: RepositoryRef,
  fetchImpl: typeof fetch = fetch,
): Promise<SourceResult> {
  return loadSource(
    {
      id: `once:${ref.repository}`,
      name: ref.repository,
      kind: 'repository',
      repository: ref.repository,
      provider: ref.provider,
      host: ref.host,
      ref: ref.ref,
    },
    fetchImpl,
  )
}

/**
 * An index, a repository or a search service is someone else's JSON, typed
 * here only by hope. A field that should be a list and is not is read as an
 * empty one, so a malformed index lists nothing rather than throwing.
 */
function list<T>(value: T[] | undefined): T[] {
  return Array.isArray(value) ? value.filter(isRecord) : []
}

const isRecord = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** A document that is not an object (null, a list, a string) reads as empty. */
function record<T>(value: unknown): T {
  return (isRecord(value) ? value : {}) as T
}

/**
 * A file name an index may list: a bare name ending in .json. Anything with a
 * path in it could point outside the repository's assessment folder.
 */
export function isAssessmentFile(name: unknown): name is string {
  return typeof name === 'string' && /^[\w.-]+\.json$/.test(name) && !name.includes('..')
}

/**
 * An entry with every field the type promises, or nothing.
 *
 * Names and versions are rendered, so one that arrived as an object would
 * crash the page that shows it; anything that is not a string is dropped or
 * replaced, and an entry with no usable address is dropped.
 */
function clean(entry: CatalogEntry): CatalogEntry[] {
  // Relative addresses (the bundled samples) are resolved against this site;
  // an absolute one must be a web address.
  const reportUrl = text(entry.reportUrl)
  const hasScheme = reportUrl !== undefined && /^[a-z][a-z0-9+.-]*:/i.test(reportUrl)
  if (!reportUrl || (hasScheme && !webAddress(reportUrl))) return []
  return [
    {
      sourceId: entry.sourceId,
      sourceName: entry.sourceName,
      softwareName: text(entry.softwareName) ?? 'Unnamed software',
      softwareVersion: text(entry.softwareVersion),
      repositoryUrl: webAddress(entry.repositoryUrl),
      reportUrl,
      assessedAt: text(entry.assessedAt),
      checks: typeof entry.checks === 'number' ? entry.checks : undefined,
    },
  ]
}
